/**
 * @file     ListingModalLinks.tsx
 * @purpose  Opens listing details in place, as a modal, on static SEO pages
 *           (building pages, location grids) instead of navigating away. Wraps
 *           any subtree and intercepts clicks on listing anchors carrying a
 *           `data-listing-key`. The anchors stay real <a href> links, so crawl
 *           paths, link equity, middle-click and "open in new tab" all survive;
 *           only the plain left-click is upgraded to a modal.
 */
'use client';

import { Fragment, useCallback, useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ListingModalScope } from '@/features/properties/components/listingModalScope';

// The quick view (gallery, spec sections, charts, VOW gate) is ~60KB gz. It
// stays out of the page bundle until a card is clicked, so grids on the home
// page and SEO hubs do not pay for it before their LCP.
const GatedListingDetailModal = dynamic(
  () => import('./GatedListingDetailModal').then((m) => m.GatedListingDetailModal),
  { ssr: false },
);

interface Props {
  children: ReactNode;
}

export function ListingModalLinks({ children }: Props) {
  const [listingKey, setListingKey] = useState<string | null>(null);
  // Stays mounted after the first open so the close transition still plays.
  const [modalLoaded, setModalLoaded] = useState(false);
  const { trackCtaClick } = useAnalytics();

  // Capture phase, deliberately. Listing cards render through next/link, whose
  // own onClick sits on the anchor and calls preventDefault() before starting
  // client-side navigation. A bubble-phase listener here would always arrive
  // after that and see defaultPrevented, so the modal never opened and the
  // visitor was routed to the detail page instead.
  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Modified clicks are the user asking for a new tab/window — leave them.
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    // The card photo gallery's arrows live inside the anchor; they page photos, not open the listing.
    if ((e.target as HTMLElement).closest?.('[data-gallery-nav]')) return;
    const anchor = (e.target as HTMLElement).closest?.('a[data-listing-key]');
    const key = anchor?.getAttribute('data-listing-key');
    if (!key) return;
    // Link navigates regardless of defaultPrevented, so the click must not
    // reach the anchor at all. That also skips the card's own CTA tracking,
    // so re-fire it here to keep the funnel intact.
    e.preventDefault();
    e.stopPropagation();
    trackCtaClick('View Property', 'listing_modal_links', anchor?.getAttribute('href') ?? undefined);
    setModalLoaded(true);
    setListingKey(key);
  }, [trackCtaClick]);

  return (
    <div onClickCapture={onClickCapture}>
      {/* A server slot arrives as an unvalidated lazy element; the keyed
          wrapper stops React's list-key warning beside the modal. */}
      <ListingModalScope.Provider value>
        <Fragment key="slot">{children}</Fragment>
      </ListingModalScope.Provider>
      {modalLoaded && (
        <GatedListingDetailModal
          listingKey={listingKey}
          open={listingKey != null}
          onClose={() => setListingKey(null)}
        />
      )}
    </div>
  );
}
